import { useState } from 'react';
import { Plus, Save, FileText, Calendar, Target } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { useApp } from '../context/AppContext';

const formatNumber = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

const getCurrentMonth = () => new Date().toISOString().slice(0, 7);

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

const TargetBudgetForm = ({ onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    month: getCurrentMonth(),
    revenue_target: '',
    marketing_budget: '',
    operating_budget: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      revenue_target: parseFloat(formData.revenue_target),
      marketing_budget: parseFloat(formData.marketing_budget),
      operating_budget: parseFloat(formData.operating_budget)
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
        <input type="month" value={formData.month} onChange={(e) => setFormData({...formData, month: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" required />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Revenue Target ($)</label>
        <input type="number" step="0.01" value={formData.revenue_target} onChange={(e) => setFormData({...formData, revenue_target: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" required placeholder="0.00" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Marketing Budget ($)</label>
          <input type="number" step="0.01" value={formData.marketing_budget} onChange={(e) => setFormData({...formData, marketing_budget: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" required placeholder="0.00" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Operating Budget ($)</label>
          <input type="number" step="0.01" value={formData.operating_budget} onChange={(e) => setFormData({...formData, operating_budget: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" required placeholder="0.00" />
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
        <button type="submit" className="flex-1 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center justify-center gap-2">
          <Save className="w-4 h-4" /> Save
        </button>
      </div>
    </form>
  );
};

const MonthlySummaryPage = () => {
  const { revenueList, expenseList, targetBudgetList, addTargetBudget, loading } = useApp();
  const [modalOpen, setModalOpen] = useState(false);

  const handleAdd = async (data) => {
    await addTargetBudget(data);
    setModalOpen(false);
  };

  // Calculate monthly data
  const monthlyData = {};
  revenueList.forEach(item => {
    const month = item.date?.slice(0, 7);
    if (month) {
      if (!monthlyData[month]) monthlyData[month] = { month, revenue: 0, expenses: 0, target: 0 };
      monthlyData[month].revenue += item.amount || 0;
    }
  });
  expenseList.forEach(item => {
    const month = item.date?.slice(0, 7);
    if (month) {
      if (!monthlyData[month]) monthlyData[month] = { month, revenue: 0, expenses: 0, target: 0 };
      monthlyData[month].expenses += item.amount || 0;
    }
  });
  targetBudgetList.forEach(item => {
    const month = item.month;
    if (month) {
      if (!monthlyData[month]) monthlyData[month] = { month, revenue: 0, expenses: 0, target: 0 };
      monthlyData[month].target = item.revenue_target || 0;
    }
  });

  const summaryData = Object.values(monthlyData)
    .map(item => ({
      ...item,
      profit: item.revenue - item.expenses,
      achievement: item.target > 0 ? ((item.revenue / item.target) * 100).toFixed(1) : 0
    }))
    .sort((a, b) => a.month.localeCompare(b.month));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Monthly Summary</h1>
          <p className="text-gray-500 text-sm">Track targets and performance by month</p>
        </div>
        <button onClick={() => setModalOpen(true)}
          className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Set Target
        </button>
      </div>

      {/* Monthly Comparison Chart */}
      <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 shadow-sm">
        <h3 className="text-gray-800 font-medium mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-red-700" /> Monthly Performance
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={summaryData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 10 }} />
            <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
            <Tooltip formatter={(value) => formatNumber(value)} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
            <Bar dataKey="revenue" fill="#10B981" name="Revenue" />
            <Bar dataKey="expenses" fill="#EF4444" name="Expenses" />
            <Bar dataKey="target" fill="#3B82F6" name="Target" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Profit Trend */}
      <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-100 shadow-sm">
        <h3 className="text-gray-800 font-medium mb-4">Profit Trend</h3>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={summaryData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 10 }} />
            <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
            <Tooltip formatter={(value) => formatNumber(value)} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
            <Line type="monotone" dataKey="profit" stroke="#C41E3A" strokeWidth={2} dot={{ fill: '#C41E3A' }} name="Net Profit" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Monthly Summary Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-red-700 to-red-800">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <Calendar className="w-5 h-5" /> Monthly Summary Table
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3 text-gray-600 font-medium">Month</th>
                <th className="text-right p-3 text-gray-600 font-medium">Target</th>
                <th className="text-right p-3 text-gray-600 font-medium">Revenue</th>
                <th className="text-right p-3 text-gray-600 font-medium">Expenses</th>
                <th className="text-right p-3 text-gray-600 font-medium">Profit</th>
                <th className="text-right p-3 text-gray-600 font-medium">Achievement</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {summaryData.map((item) => (
                <tr key={item.month} className="hover:bg-gray-50">
                  <td className="p-3 text-gray-800 font-medium">{item.month}</td>
                  <td className="p-3 text-right text-blue-600">{item.target > 0 ? formatNumber(item.target) : '-'}</td>
                  <td className="p-3 text-right text-emerald-600 font-medium">{formatNumber(item.revenue)}</td>
                  <td className="p-3 text-right text-red-600">{formatNumber(item.expenses)}</td>
                  <td className={`p-3 text-right font-bold ${item.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatNumber(item.profit)}
                  </td>
                  <td className="p-3 text-right">
                    {item.target > 0 ? (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium RM{
                        parseFloat(item.achievement) >= 100 ? 'bg-emerald-100 text-emerald-700' :
                        parseFloat(item.achievement) >= 80 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {item.achievement}%
                      </span>
                    ) : '-'}
                  </td>
                </tr>
              ))}
              {summaryData.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-gray-500">No data yet. Add revenue, expenses, or set targets.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Target Budget List */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <Target className="w-5 h-5 text-red-700" /> Target Budgets
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3 text-gray-600 font-medium">Month</th>
                <th className="text-right p-3 text-gray-600 font-medium">Revenue Target</th>
                <th className="text-right p-3 text-gray-600 font-medium">Marketing Budget</th>
                <th className="text-right p-3 text-gray-600 font-medium">Operating Budget</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {targetBudgetList.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="p-3 text-gray-800 font-medium">{item.month}</td>
                  <td className="p-3 text-right text-blue-600 font-medium">{formatNumber(item.revenue_target)}</td>
                  <td className="p-3 text-right text-gray-600">{formatNumber(item.marketing_budget)}</td>
                  <td className="p-3 text-right text-gray-600">{formatNumber(item.operating_budget)}</td>
                </tr>
              ))}
              {targetBudgetList.length === 0 && (
                <tr><td colSpan={4} className="p-8 text-center text-gray-500">No targets set yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Set Monthly Target">
        <TargetBudgetForm onSubmit={handleAdd} onClose={() => setModalOpen(false)} />
      </Modal>
    </div>
  );
};

export default MonthlySummaryPage;
